import type { VideoLayer } from './types';

/** Stable id for the auto-seeded facecam picture-in-picture suggestion, so it
 *  can be recognised (and never double-seeded) across renders. */
export const FACECAM_SUGGESTION_ID = 'facecam-suggested';

export interface FacecamSuggestionInput {
  /** URL of the pre-cropped facecam video (whole-source, from detection). */
  facecamUrl?: string | null;
  /** Full source duration in seconds — the facecam video's intrinsic length.
   *  Must exceed the clip's source end or normalizeLayer would clamp
   *  trimStart and break the alignment. */
  sourceDurationSec?: number | null;
  /** Source-time start of the clip's (single) segment, in seconds. */
  sourceStartSec?: number | null;
  /** Output length of the clip in seconds. */
  clipDurationSec?: number | null;
  /** How many segments the clip has. The suggestion only aligns on a single
   *  continuous segment (a plain upload layer plays linearly from trimStart). */
  segmentCount?: number | null;
  /** Whether the clip is being framed vertically. A corner PiP only helps the
   *  9:16 crop; on a horizontal render it would just duplicate the streamer. */
  isPortrait?: boolean;
}

export interface FacecamSpan {
  start_time: number;
  end_time: number;
}

export interface FacecamSuggestionsInput {
  facecamUrl?: string | null;
  sourceDurationSec?: number | null;
  segments?: Array<{ start: number; end: number }> | null;
  facecamSpans?: FacecamSpan[] | null;
  isPortrait?: boolean;
}

/**
 * Build the auto-seeded facecam PiP layer for a stream clip, or null.
 *
 * Detection cropped the streamer's corner inset into its own video and put the
 * URL on scene-detection. This surfaces it in the editor as a real,
 * user-editable layer from the start — visible in the preview, movable,
 * deletable — rather than a silent export-time injection the user never sees.
 *
 * Gated to portrait + single-segment for the same reason the render path is:
 * the facecam video is whole-source, so a plain upload layer plays it linearly
 * from trimStart. That aligns frame-for-frame with one continuous segment but
 * would drift across the cuts of a multi-segment clip.
 */
export function buildFacecamSuggestion(input: FacecamSuggestionInput): VideoLayer | null {
  const {
    facecamUrl,
    sourceDurationSec,
    sourceStartSec,
    clipDurationSec,
    segmentCount,
    isPortrait,
  } = input;

  if (!facecamUrl || typeof facecamUrl !== 'string') return null;
  if (facecamUrl.startsWith('blob:')) return null;
  if (!isPortrait) return null;
  if (segmentCount !== 1) return null;

  const clipDur = Number(clipDurationSec);
  const srcStart = Number(sourceStartSec);
  const srcDur = Number(sourceDurationSec);
  if (!Number.isFinite(clipDur) || clipDur <= 0) return null;
  if (!Number.isFinite(srcStart) || srcStart < 0) return null;
  // The facecam video spans the whole source; its duration must cover the
  // clip's source window or the layer can't play the aligned footage.
  if (!Number.isFinite(srcDur) || srcDur < srcStart + clipDur) return null;

  return {
    id: FACECAM_SUGGESTION_ID,
    name: 'Facecam',
    type: 'video',
    url: facecamUrl,
    start: 0,
    end: clipDur,
    opacity: 1,
    // Top-left corner, matched to the facecam crop's ~4:3 aspect in a 9:16 frame.
    rect: { x: 0.04, y: 0.035, w: 0.42, h: 0.175 },
    rotation: 0,
    uploading: false,
    volume: 0,
    mediaDuration: srcDur,
    speed: 1,
    // Play the whole-source facecam from this clip's source start.
    trimStart: srcStart,
    trimEnd: srcStart + clipDur,
    source: 'upload',
  };
}

const roundTimeline = (value: number): number => Math.round(value * 1000) / 1000;

/**
 * Build editable PiP layers for the intersections between retained clip
 * segments and detector-provided source-time facecam spans.
 *
 * Each overlap becomes an independent layer window. This keeps the cropped
 * whole-source facecam frame-accurate across removed source gaps: output time
 * advances continuously, while trimStart/trimEnd jump to the next retained
 * source interval.
 *
 * Missing spans mean an older detection payload. Preserve its historical
 * single-segment behavior through buildFacecamSuggestion.
 */
export function buildFacecamSuggestions(input: FacecamSuggestionsInput): VideoLayer[] {
  const segments = Array.isArray(input.segments)
    ? input.segments.filter((segment) => (
      Number.isFinite(segment?.start)
      && Number.isFinite(segment?.end)
      && segment.start >= 0
      && segment.end > segment.start
    ))
    : [];
  if (!segments.length) return [];

  if (!Array.isArray(input.facecamSpans)) {
    if (segments.length !== 1) return [];
    const segment = segments[0];
    const legacy = buildFacecamSuggestion({
      facecamUrl: input.facecamUrl,
      sourceDurationSec: input.sourceDurationSec,
      sourceStartSec: segment.start,
      clipDurationSec: segment.end - segment.start,
      segmentCount: 1,
      isPortrait: input.isPortrait,
    });
    return legacy ? [legacy] : [];
  }

  const spans = input.facecamSpans
    .filter((span) => (
      Number.isFinite(span?.start_time)
      && Number.isFinite(span?.end_time)
      && span.start_time >= 0
      && span.end_time > span.start_time
    ))
    .sort((a, b) => a.start_time - b.start_time);
  if (!spans.length) return [];

  const layers: VideoLayer[] = [];
  let outputOffset = 0;
  for (const segment of segments) {
    for (const span of spans) {
      const sourceStart = Math.max(segment.start, span.start_time);
      const sourceEnd = Math.min(segment.end, span.end_time);
      if (sourceEnd <= sourceStart) continue;

      const layer = buildFacecamSuggestion({
        facecamUrl: input.facecamUrl,
        sourceDurationSec: input.sourceDurationSec,
        sourceStartSec: sourceStart,
        clipDurationSec: sourceEnd - sourceStart,
        segmentCount: 1,
        isPortrait: input.isPortrait,
      });
      if (!layer) continue;

      const outputStart = outputOffset + sourceStart - segment.start;
      layer.id = layers.length === 0
        ? FACECAM_SUGGESTION_ID
        : `${FACECAM_SUGGESTION_ID}-${layers.length}`;
      layer.start = roundTimeline(outputStart);
      layer.end = roundTimeline(outputOffset + sourceEnd - segment.start);
      layer.trimStart = roundTimeline(sourceStart);
      layer.trimEnd = roundTimeline(sourceEnd);
      layers.push(layer);
    }
    outputOffset += segment.end - segment.start;
  }

  return layers;
}
